<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'

import TextInput from '../../../../components/ui/form/TextInput.vue'
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import FileUpload from '../../../../components/ui/form/FileUpload.vue'
import IconComponent from '../../../../components/ui/common/IconComponent.vue'

/**
 * Singleton page for the storefront's "Ecommerce Setup". Loads the lone row
 * via `singleton` (creating one server-side if absent), then writes
 * everything — including all six child collections — back through `update`.
 *
 * Not a modal: the previous list+modal pattern doesn't fit a one-row-per-
 * tenant setup, and the form has enough surface that a side-scrolling modal
 * stops being usable. Layout follows the CompanyInformationListView
 * precedent, which is also "ListView" by file name but is actually a page.
 */

const endPoint = '/clients/system/company/ecommerce-setups/'

const loading = ref(true)
const saving = ref(false)
const data = ref<any>(null)

/**
 * Ecommerce setup is a singleton settings page — always editable, no
 * "view" mode the way modal-style forms in the codebase have. Kept as a
 * ref anyway because `FileUpload` and the other form atoms across the
 * codebase expect `:readonly` to be present in their standard call site,
 * and matching that signature keeps the props order consistent with
 * CompanyInformationListView.
 */
const isReadOnly = ref(false)

const channelTypes = [
  { id: 'Website', name: 'Website' },
  { id: 'WhatsApp', name: 'WhatsApp' },
  { id: 'Facebook', name: 'Facebook' },
  { id: 'Instagram', name: 'Instagram' },
  { id: 'Other', name: 'Other' },
]
const currencies = [
  { id: 'MVR', name: 'MVR' },
  { id: 'USD', name: 'USD' },
]

const sections = [
  { id: 'identity', label: 'Identity' },
  { id: 'branding', label: 'Branding' },
  { id: 'announcement', label: 'Announcement' },
  { id: 'hero', label: 'Hero' },
  { id: 'value_props', label: 'Value props' },
  { id: 'nav', label: 'Top navigation' },
  { id: 'footer', label: 'Footer' },
  { id: 'pages', label: 'Pages' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'contact', label: 'Contact & social' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'catalog', label: 'Catalog display' },
  { id: 'seo', label: 'SEO' },
  { id: 'notifications', label: 'Order notifications' },
  { id: 'quotation_ops', label: 'Quotation operations' },
  { id: 'policies', label: 'Policies' },
  { id: 'featured', label: 'Featured content' },
  { id: 'operational', label: 'Operational' },
]

/**
 * Active section. Only the picked one renders so the page stays legible
 * instead of stacking 17 long sections at once. Default to the first.
 */
const activeSection = ref<string>('identity')

function pickSection(id: string) {
  activeSection.value = id
  // Reset the scroll so the picked section starts at the top of the viewport
  // even if the previous one was scrolled mid-way.
  requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
}

function onSectionChange(e: Event) {
  pickSection((e.target as HTMLSelectElement).value)
}

const activeLabel = computed(
  () => sections.find((s) => s.id === activeSection.value)?.label ?? '',
)

onMounted(async () => {
  loading.value = true
  try {
    const r = await api.post(endPoint + 'singleton', {})
    if (r.success) {
      data.value = withChildDefaults(r.data || {})
    }
  } finally {
    loading.value = false
  }
})

/**
 * Make sure every child collection is a real array so v-for / v-model are
 * happy even on a fresh singleton row that hasn't been saved yet.
 */
function withChildDefaults(d: any): any {
  d.hero_slides = d.hero_slides || []
  d.pages = d.pages || []
  d.value_props = d.value_props || []
  d.nav_items = d.nav_items || []
  d.footer_columns = (d.footer_columns || []).map((c: any) => ({
    ...c,
    links: c.links || [],
  }))
  d.faqs = d.faqs || []
  return d
}

async function save() {
  if (!data.value?.outlets_id) {
    Toast.show('Pick an outlet before saving.', 'warning')
    return
  }
  if (!data.value?.type) {
    Toast.show('Channel type is required.', 'warning')
    return
  }
  saving.value = true
  try {
    const r = await api.post(endPoint + 'update', data.value)
    if (r.success) {
      Toast.show('Storefront saved.')
      data.value = withChildDefaults(r.data || {})
    }
  } finally {
    saving.value = false
  }
}

/* ─────────────────  child-row helpers  ───────────────── */

function addHeroSlide() {
  data.value.hero_slides.push({
    image: '', eyebrow: '', headline: '', subheadline: '',
    primary_cta_label: '', primary_cta_link: '',
    secondary_cta_label: '', secondary_cta_link: '',
    bg_color: '', text_color: '', alignment: 'left',
    sort_order: data.value.hero_slides.length, active: true,
  })
}
function removeHeroSlide(i: number) { data.value.hero_slides.splice(i, 1) }
function moveHeroSlide(i: number, dir: -1 | 1) {
  const arr = data.value.hero_slides
  const j = i + dir
  if (j < 0 || j >= arr.length) return
  ;[arr[i], arr[j]] = [arr[j], arr[i]]
  arr.forEach((s: any, idx: number) => (s.sort_order = idx))
}

function addValueProp() {
  data.value.value_props.push({
    icon: '', title: '', body: '',
    sort_order: data.value.value_props.length, active: true,
  })
}
function removeValueProp(i: number) { data.value.value_props.splice(i, 1) }

/**
 * Live preview helper for the value-prop SVG icon textarea. Strips the
 * common XSS vectors (script blocks, on* handlers, javascript: URIs) before
 * the markup is passed to v-html, so a pasted icon can't introduce script
 * even while the admin is still typing. Returns '' when the input doesn't
 * look like SVG so the empty-state placeholder renders instead.
 */
function svgPreview(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s || !/^<svg[\s>]/i.test(s)) return ''
  return s
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript\s*:/gi, '')
}

function addNavItem() {
  data.value.nav_items.push({
    label: '', link: '',
    sort_order: data.value.nav_items.length, active: true,
  })
}
function removeNavItem(i: number) { data.value.nav_items.splice(i, 1) }

function addFooterColumn() {
  data.value.footer_columns.push({
    heading: '', links: [],
    sort_order: data.value.footer_columns.length, active: true,
  })
}
function removeFooterColumn(i: number) { data.value.footer_columns.splice(i, 1) }
function addFooterLink(colIdx: number) {
  data.value.footer_columns[colIdx].links.push({
    label: '', link: '',
    sort_order: data.value.footer_columns[colIdx].links.length, active: true,
  })
}
function removeFooterLink(colIdx: number, linkIdx: number) {
  data.value.footer_columns[colIdx].links.splice(linkIdx, 1)
}

function addPage() {
  data.value.pages.push({
    slug: '', title: '', body_html: '',
    show_in_footer: false, footer_column: '',
    sort_order: data.value.pages.length, active: true,
  })
}
function removePage(i: number) { data.value.pages.splice(i, 1) }

function addFaq() {
  data.value.faqs.push({
    question: '', answer: '',
    sort_order: data.value.faqs.length, active: true,
  })
}
function removeFaq(i: number) { data.value.faqs.splice(i, 1) }

const hasRecord = computed(() => !!data.value)
</script>

<template>
  <div class="min-h-full">
    <!-- ──  Sticky save bar  ────────────────────────────────────── -->
    <div class="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div class="px-6 py-3 flex items-center gap-3">
        <h1 class="text-lg font-semibold text-gray-900 dark:text-white">Ecommerce setup</h1>
        <span class="text-xs text-gray-500 dark:text-gray-400 hidden md:inline">
          Storefront identity, layout, and dynamic content.
        </span>
        <div class="flex-1" />
        <button
          type="button"
          class="px-4 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          :disabled="loading || saving || !hasRecord"
          @click="save"
        >
          <IconComponent v-if="!saving" name="Save" class="w-4 h-4" />
          <span v-else class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="p-12 text-center text-gray-500">Loading…</div>

    <div v-else-if="hasRecord" class="grid grid-cols-12 gap-6 p-6">
      <!-- ──  Side nav  ───────────────────────────────────────── -->
      <aside class="hidden lg:block col-span-2 sticky top-16 self-start">
        <nav class="space-y-1 text-sm">
          <button
            v-for="s in sections" :key="s.id"
            type="button"
            class="block w-full text-left px-3 py-1.5 rounded transition"
            :class="
              activeSection === s.id
                ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
            "
            @click="pickSection(s.id)"
          >{{ s.label }}</button>
        </nav>
      </aside>

      <!-- ──  Sectioned form  ─────────────────────────────────── -->
      <div class="col-span-12 lg:col-span-10">
        <!-- Mobile section picker — replaces the hidden side nav on small screens. -->
        <div class="lg:hidden mb-4">
          <label class="block text-xs uppercase tracking-wide text-gray-500 mb-1">Section</label>
          <select
            :value="activeSection"
            class="appearance-none w-full border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm"
            @change="onSectionChange"
          >
            <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.label }}</option>
          </select>
        </div>

        <!-- Single-section heading -->
        <div class="mb-4 lg:hidden flex items-baseline gap-2 text-xs text-gray-500">
          <span>Showing</span>
          <span class="font-semibold text-gray-800 dark:text-gray-200">{{ activeLabel }}</span>
        </div>

        <div class="space-y-10">

        <!-- Identity -->
        <section v-show="activeSection === 'identity'" id="sec-identity">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Identity</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <SearchSelect v-model="data.outlets_id" label="Outlet" end-point="system/company/outlets" />
            <!--
              Sales channel stamped on every storefront-created sales order
              and quotation so they roll up under the same channel as the rest
              of the POS data (alongside outlets_id below and the
              document_users_id beside it).
            -->
            <SearchSelect v-model="data.sales_channels_id" label="Sales channel" end-point="revenue/master-data/sales-channels" />
            <SearchSelect v-model="data.type" label="Channel type" :options="channelTypes" />
            <!--
              Every sales order / quotation / cart the storefront creates is
              written with this user as `users_id`. Reporting and permission
              audits depend on it pointing at a real POS staff user — leave
              blank and those records arrive un-attributed.
            -->
            <SearchSelect
              v-model="data.document_users_id"
              label="User for storefront documents"
              end-point="system/security/users"
            />
            <CheckInput v-model="data.public_storefront_enabled" label="Public storefront enabled" />
            <TextInput v-model="data.site_name" label="Site name" placeholder="Vaaly Traders" />
            <TextInput v-model="data.tagline" label="Tagline" placeholder="Wholesale, simplified." />
            <TextInput v-model="data.copyright_text" label="Copyright text" placeholder="(falls back to © year + site name)" />
          </div>
          <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            The selected user is recorded as the creator on every sales order
            and quotation submitted from the storefront.
          </p>
        </section>

        <!-- Branding -->
        <section v-show="activeSection === 'branding'" id="sec-branding">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Branding</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <FileUpload
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              label="Logo"
              :max-size="5"
              :multiple="false"
              :readonly="isReadOnly"
              :image="true"
              path="ecommerce_setups"
              v-model="data.logo"
            />
            <FileUpload
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              label="Favicon"
              :max-size="5"
              :multiple="false"
              :readonly="isReadOnly"
              :image="true"
              path="ecommerce_setups"
              v-model="data.favicon"
            />
            <FileUpload
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              label="Hero image"
              :max-size="5"
              :multiple="false"
              :readonly="isReadOnly"
              :image="true"
              path="ecommerce_setups"
              v-model="data.hero_image"
            />
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand color</label>
              <input v-model="data.brand_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
            </div>
          </div>
        </section>

        <!-- Announcement bar -->
        <section v-show="activeSection === 'announcement'" id="sec-announcement">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Announcement bar</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <CheckInput v-model="data.announcement_enabled" label="Show announcement strip" />
            <TextInput v-model="data.announcement_text" label="Announcement text" placeholder="Free delivery on orders above MVR 5,000" class="md:col-span-2" />
          </div>
        </section>

        <!-- Hero slides (carousel) -->
        <section v-show="activeSection === 'hero'" id="sec-hero">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Hero slides</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addHeroSlide">+ Add slide</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">
            Each slide has its own image, copy, and CTA buttons. Slides render in sort order as a carousel
            on the home page. One slide = a single static hero.
          </p>

          <div v-if="data.hero_slides.length === 0" class="text-sm text-gray-400 italic">
            No hero slides yet. Click <em>Add slide</em> to create the first.
          </div>

          <div
            v-for="(s, i) in data.hero_slides"
            :key="i"
            class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
          >
            <!-- Slide header bar: order, up/down/remove -->
            <div class="flex items-center justify-between mb-3">
              <div class="text-xs uppercase tracking-wide font-semibold text-gray-500">
                Slide {{ i + 1 }}
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === 0"
                  aria-label="Move slide up"
                  @click="moveHeroSlide(i, -1)"
                >
                  <IconComponent name="ChevronUp" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"
                  :disabled="i === data.hero_slides.length - 1"
                  aria-label="Move slide down"
                  @click="moveHeroSlide(i, 1)"
                >
                  <IconComponent name="ChevronDown" class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                  aria-label="Remove slide"
                  @click="removeHeroSlide(i)"
                >
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Slide media + copy -->
            <div class="grid md:grid-cols-3 gap-4">
              <FileUpload
                accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
                label="Slide image"
                :max-size="5"
                :multiple="false"
                :readonly="isReadOnly"
                :image="true"
                path="ecommerce_setups"
                v-model="s.image"
              />
              <TextInput v-model="s.eyebrow" label="Eyebrow" placeholder="ESTABLISHED 1982" />
              <SearchSelect
                v-model="s.alignment"
                label="Text alignment"
                :options="[{ id: 'left', name: 'Left' }, { id: 'center', name: 'Center' }, { id: 'right', name: 'Right' }]"
              />
              <TextInput v-model="s.headline" label="Headline" placeholder="Wholesale supplies, delivered island-wide" class="md:col-span-2" />
              <CheckInput v-model="s.active" label="Active" />
              <TextAreaInput v-model="s.subheadline" label="Sub-headline" :rows="2" class="md:col-span-3" />
            </div>

            <!-- CTAs -->
            <div class="mt-4 grid md:grid-cols-2 gap-4">
              <div class="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">Primary button</div>
                <div class="grid grid-cols-2 gap-3">
                  <TextInput v-model="s.primary_cta_label" label="Label" placeholder="Shop now" />
                  <TextInput v-model="s.primary_cta_link" label="Link" placeholder="/search" />
                </div>
              </div>
              <div class="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div class="text-xs uppercase tracking-wide text-gray-500 mb-2">Secondary button</div>
                <div class="grid grid-cols-2 gap-3">
                  <TextInput v-model="s.secondary_cta_label" label="Label" placeholder="Bulk pricing" />
                  <TextInput v-model="s.secondary_cta_link" label="Link" placeholder="/about" />
                </div>
              </div>
            </div>

            <!-- Colours -->
            <div class="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Background colour</label>
                <div class="flex items-center gap-3">
                  <input v-model="s.bg_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
                  <input v-model="s.bg_color" type="text" placeholder="#0d6efd" class="appearance-none flex-1 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm font-mono" />
                </div>
                <p class="text-xs text-gray-500 mt-1">Used behind the image — fallback when no slide image is set.</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Text colour</label>
                <div class="flex items-center gap-3">
                  <input v-model="s.text_color" type="color" class="h-9 w-16 cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 bg-transparent" />
                  <input v-model="s.text_color" type="text" placeholder="#ffffff" class="appearance-none flex-1 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Value props -->
        <section v-show="activeSection === 'value_props'" id="sec-value_props">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Value-prop cards</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addValueProp">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">
            Cards under the hero (Island-wide delivery, Net-30, etc.). Paste an
            <code>&lt;svg&gt;</code> for the icon — the preview swatch on the
            left updates live. Grab clean stroke icons from
            <a href="https://lucide.dev" target="_blank" rel="noopener" class="text-blue-600 hover:underline">lucide.dev</a> or
            <a href="https://heroicons.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">heroicons.com</a>.
          </p>
          <div v-if="data.value_props.length === 0" class="text-sm text-gray-400 italic">
            No value-prop cards yet. Click <em>Add</em> to create the first.
          </div>

          <div
            v-for="(v, i) in data.value_props"
            :key="i"
            class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
          >
            <div class="grid md:grid-cols-12 gap-4 items-start">
              <!-- Live preview swatch -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview</label>
                <div
                  class="w-full aspect-square rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-400 overflow-hidden p-3"
                >
                  <div
                    v-if="svgPreview(v.icon)"
                    v-html="svgPreview(v.icon)"
                    class="w-full h-full [&_svg]:w-full [&_svg]:h-full text-blue-600"
                    aria-hidden="true"
                  />
                  <span v-else class="text-xs text-center leading-tight">Paste<br/>SVG</span>
                </div>
              </div>

              <!-- Title / Body / Order / Remove -->
              <div class="md:col-span-10 grid md:grid-cols-12 gap-3">
                <TextInput v-model="v.title" label="Title" class="md:col-span-5" />
                <TextInput v-model="v.body" label="Body" class="md:col-span-5" />
                <NumberInput v-model="v.sort_order" label="Order" :show-controls="false" class="md:col-span-1" />
                <div class="md:col-span-1 pt-7 flex justify-end">
                  <button type="button" class="text-red-500 hover:text-red-700" @click="removeValueProp(i)">
                    <IconComponent name="Trash2" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <!-- SVG textarea, full width -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon (SVG markup)</label>
              <textarea
                v-model="v.icon"
                :disabled="isReadOnly"
                rows="4"
                placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">…</svg>'
                spellcheck="false"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-transparent rounded-md text-xs font-mono focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p class="text-xs text-gray-500 mt-1">
                Use <code>stroke="currentColor"</code> or <code>fill="currentColor"</code> so the icon picks up
                the card's text colour automatically.
              </p>
            </div>
          </div>
        </section>

        <!-- Top nav -->
        <section v-show="activeSection === 'nav'" id="sec-nav">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Top navigation</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addNavItem">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">Items shown alongside Home in the header.</p>
          <div v-if="data.nav_items.length === 0" class="text-sm text-gray-400 italic">Only the home link will render in the header.</div>
          <div v-for="(n, i) in data.nav_items" :key="i" class="grid md:grid-cols-12 gap-3 items-start mb-3">
            <TextInput v-model="n.label" label="Label" class="md:col-span-4" />
            <TextInput v-model="n.link" label="Link" placeholder="/about" class="md:col-span-5" />
            <NumberInput v-model="n.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
            <div class="md:col-span-1 pt-7">
              <button type="button" class="text-red-500 hover:text-red-700" @click="removeNavItem(i)">
                <IconComponent name="Trash2" class="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        <!-- Footer columns + links -->
        <section v-show="activeSection === 'footer'" id="sec-footer">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Footer columns</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addFooterColumn">+ Add column</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">Each column has a heading and a list of links. Order columns left → right with sort order.</p>
          <div v-if="data.footer_columns.length === 0" class="text-sm text-gray-400 italic">No footer columns. The footer will render brand + tagline only.</div>
          <div v-for="(c, ci) in data.footer_columns" :key="ci" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="c.heading" label="Heading" placeholder="Shop / Account / Company" class="md:col-span-5" />
              <NumberInput v-model="c.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
              <div class="md:col-span-5 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700 inline-flex items-center gap-1" @click="removeFooterColumn(ci)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                  <span class="text-xs">Remove column</span>
                </button>
              </div>
            </div>
            <div class="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              <div class="flex items-baseline justify-between mb-2">
                <span class="text-xs uppercase tracking-wide text-gray-500">Links</span>
                <button type="button" class="text-xs text-blue-600 hover:underline" @click="addFooterLink(ci)">+ Add link</button>
              </div>
              <div v-for="(l, li) in c.links" :key="li" class="grid md:grid-cols-12 gap-2 items-start mb-2">
                <TextInput v-model="l.label" label="Label" class="md:col-span-4" />
                <TextInput v-model="l.link" label="URL" class="md:col-span-5" />
                <NumberInput v-model="l.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
                <div class="md:col-span-1 pt-7">
                  <button type="button" class="text-red-500 hover:text-red-700" @click="removeFooterLink(ci, li)">
                    <IconComponent name="Trash2" class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Pages -->
        <section v-show="activeSection === 'pages'" id="sec-pages">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">Content pages</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addPage">+ Add</button>
          </div>
          <p class="text-xs text-gray-500 mb-4">About, Contact, FAQ, and any custom page. Slug becomes the URL: <code>/p/&lt;slug&gt;</code>.</p>
          <div v-if="data.pages.length === 0" class="text-sm text-gray-400 italic">No custom pages yet.</div>
          <div v-for="(p, i) in data.pages" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="p.slug" label="Slug" placeholder="about" class="md:col-span-3" />
              <TextInput v-model="p.title" label="Title" placeholder="About us" class="md:col-span-4" />
              <TextInput v-model="p.footer_column" label="Footer column heading" class="md:col-span-3" />
              <CheckInput v-model="p.show_in_footer" label="Show in footer" class="md:col-span-1 pt-7" />
              <div class="md:col-span-1 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700" @click="removePage(i)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>
            <TextAreaInput v-model="p.body_html" label="Body (HTML)" :rows="5" class="mt-3" />
          </div>
        </section>

        <!-- FAQs -->
        <section v-show="activeSection === 'faqs'" id="sec-faqs">
          <div class="flex items-baseline justify-between mb-3">
            <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200">FAQs</h2>
            <button type="button" class="text-sm text-blue-600 hover:underline" @click="addFaq">+ Add</button>
          </div>
          <div v-if="data.faqs.length === 0" class="text-sm text-gray-400 italic">No FAQs.</div>
          <div v-for="(f, i) in data.faqs" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4">
            <div class="grid md:grid-cols-12 gap-3 items-start">
              <TextInput v-model="f.question" label="Question" class="md:col-span-9" />
              <NumberInput v-model="f.sort_order" label="Order" :show-controls="false" class="md:col-span-2" />
              <div class="md:col-span-1 pt-7 flex justify-end">
                <button type="button" class="text-red-500 hover:text-red-700" @click="removeFaq(i)">
                  <IconComponent name="Trash2" class="w-4 h-4" />
                </button>
              </div>
            </div>
            <TextAreaInput v-model="f.answer" label="Answer" :rows="3" class="mt-3" />
          </div>
        </section>

        <!-- Contact & social -->
        <section v-show="activeSection === 'contact'" id="sec-contact">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Contact & social</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.support_email" label="Support email" type="email" />
            <TextInput v-model="data.support_phone" label="Support phone" />
            <TextInput v-model="data.whatsapp_number" label="WhatsApp number" />
            <TextInput v-model="data.physical_address" label="Physical address" />
            <TextInput v-model="data.facebook_url" label="Facebook URL" />
            <TextInput v-model="data.instagram_url" label="Instagram URL" />
            <TextInput v-model="data.tiktok_url" label="TikTok URL" />
          </div>
        </section>

        <!-- Commerce behaviour -->
        <section v-show="activeSection === 'commerce'" id="sec-commerce">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Commerce behaviour</h2>
          <div class="grid md:grid-cols-3 gap-4">
            <SearchSelect v-model="data.default_currency" label="Default currency" :options="currencies" />
            <SearchSelect v-model="data.default_price_lists_id" label="Default price list" end-point="finance/master-data/price-lists" />
            <SearchSelect v-model="data.default_customer_groups_id" label="Default customer group" end-point="revenue/master-data/customer-groups" />
            <!--
              The POS user every storefront-created document (sales order, sales
              quotation, cart, …) gets attributed to. Required for clean
              reporting / permission audits — without it the connector falls
              back to NULL and downstream code can't tell who "made" the
              record. The list comes from the same user endpoint other forms
              use (stock adjustments, vendor buyer-of-record, etc.).
            -->
            <SearchSelect v-model="data.document_users_id" label="Document attribution user" end-point="system/security/users" />
            <NumberInput v-model="data.minimum_order_amount" label="Minimum order amount" :min="0" :show-controls="false" />
            <CheckInput v-model="data.allow_guest_checkout" label="Allow guest checkout" />
            <CheckInput v-model="data.show_prices_to_public" label="Show prices to public" />
            <CheckInput v-model="data.tax_inclusive_pricing" label="Tax-inclusive pricing" />
            <CheckInput v-model="data.only_selected_items" label="Only selected items" />
          </div>
        </section>

        <!-- Catalog display -->
        <section v-show="activeSection === 'catalog'" id="sec-catalog">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Catalog display</h2>
          <div class="grid md:grid-cols-4 gap-4">
            <NumberInput v-model="data.products_per_page" label="Products per page" :min="1" />
            <NumberInput v-model="data.no_of_top_offer_products" label="Top offers count" :min="0" />
            <NumberInput v-model="data.no_of_best_selling_products" label="Best sellers count" :min="0" />
            <NumberInput v-model="data.no_of_new_arrival_products" label="New arrivals count" :min="0" />
            <CheckInput v-model="data.show_out_of_stock_products" label="Show out-of-stock" />
            <CheckInput v-model="data.allow_pre_orders" label="Allow pre-orders" />
            <CheckInput v-model="data.show_points" label="Show loyalty points" />
            <CheckInput v-model="data.show_reviews" label="Show reviews" />
          </div>
        </section>

        <!-- SEO -->
        <section v-show="activeSection === 'seo'" id="sec-seo">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">SEO</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.meta_title" label="Default meta title" />
            <TextInput v-model="data.meta_description" label="Default meta description" />
            <TextInput v-model="data.google_analytics_id" label="Google Analytics ID" placeholder="G-XXXXXXXXXX" />
            <TextInput v-model="data.meta_pixel_id" label="Meta Pixel ID" />
          </div>
        </section>

        <!-- Notifications -->
        <section v-show="activeSection === 'notifications'" id="sec-notifications">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Order notifications</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.order_admin_email" label="Admin notification email" type="email" />
            <TextAreaInput v-model="data.order_sms_template" label="Order SMS template" :rows="3" />
            <CheckInput v-model="data.send_order_email" label="Email admin on new order" />
            <CheckInput v-model="data.send_order_sms" label="SMS admin on new order" />
          </div>
        </section>

        <!-- Quotation operations: who hears about storefront quotation events -->
        <section v-show="activeSection === 'quotation_ops'" id="sec-quotation_ops">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Quotation operations</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Picks who gets notified when a storefront quotation is created or its
            payment is completed. The connector emails every user listed below,
            and also the staff user bound to the allocated POS counter.
          </p>
          <div class="grid md:grid-cols-2 gap-4">
            <!--
              Comma-separated `users.id` list. Kept as a string so the field
              persists cleanly through the existing JSON pipeline without an
              extra multi-select component.
            -->
            <TextInput
              v-model="data.notification_user_ids"
              label="Notification user IDs (comma-separated)"
              placeholder="3, 5, 12"
            />
            <SearchSelect
              v-model="data.notification_outlet_pos_stations_id"
              label="Allocated POS counter"
              end-point="system/company/outlet-pos-stations"
            />
          </div>
        </section>

        <!-- Policies -->
        <section v-show="activeSection === 'policies'" id="sec-policies">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Policies</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextAreaInput v-model="data.terms_html" label="Terms & conditions (HTML)" :rows="5" />
            <TextAreaInput v-model="data.privacy_html" label="Privacy policy (HTML)" :rows="5" />
            <TextAreaInput v-model="data.return_policy_html" label="Return policy (HTML)" :rows="5" />
            <TextAreaInput v-model="data.shipping_policy_html" label="Shipping policy (HTML)" :rows="5" />
          </div>
        </section>

        <!-- Featured content -->
        <section v-show="activeSection === 'featured'" id="sec-featured">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Featured content</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <TextInput v-model="data.featured_categories_ids" label="Featured category IDs (comma-separated)" />
            <TextInput v-model="data.featured_brands_ids" label="Featured brand IDs (comma-separated)" />
          </div>
        </section>

        <!-- Operational -->
        <section v-show="activeSection === 'operational'" id="sec-operational">
          <h2 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Operational</h2>
          <div class="grid md:grid-cols-2 gap-4">
            <CheckInput v-model="data.maintenance_mode" label="Maintenance mode" />
            <TextInput v-model="data.maintenance_message" label="Maintenance message" />
            <CheckInput v-model="data.active" label="Active" />
          </div>
        </section>

        </div>
      </div>
    </div>
  </div>
</template>
